import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, RotateCcw, Volume2, VolumeX, Captions, ChevronDown } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { parseCaptions, cueAt, captionsToTranscript } from "../../lib/captions";

const SESSION_KEY = "cass_cassie_intro_played";

// Visitors who have asked not to be moved around, or who are paying for data,
// get the poster and a play button instead of anything starting on its own.
const quietMode = () =>
  (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ||
  !!(navigator.connection && navigator.connection.saveData);

export default function CassieVideoIntro({ intro }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [showCaptions, setShowCaptions] = useState(true);
  const [time, setTime] = useState(0);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  // The frame takes the shape of whatever clip is uploaded, so nothing gets cropped off.
  const [ratio, setRatio] = useState(null);

  const cues = useMemo(() => parseCaptions(intro?.captions), [intro?.captions]);
  const caption = showCaptions ? cueAt(cues, time) : "";
  const transcript = (intro?.transcript || "").trim() || captionsToTranscript(intro?.captions);
  const autoplayAllowed = intro?.autoplay !== false && !quietMode();
  const src = mediaUrl(intro?.video);
  const poster = intro?.poster ? mediaUrl(intro.poster) : (intro?.video ? mediaUrl(`${intro.video}/poster`) : "");

  // Plays once per browser session, and only while actually on screen.
  useEffect(() => {
    const el = videoRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;
    const alreadyPlayed = sessionStorage.getItem(SESSION_KEY) === "1";

    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        if (!el.paused) el.pause();
        return;
      }
      if (autoplayAllowed && !alreadyPlayed && !started) {
        el.muted = true;
        el.play().then(() => {
          setStarted(true);
          sessionStorage.setItem(SESSION_KEY, "1");
        }).catch(() => {});
      }
    }, { threshold: 0.4 });

    obs.observe(wrap);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayAllowed, started]);

  // Where the browser won't give us the clip's dimensions, the poster shape is a safe stand-in.
  useEffect(() => {
    if (ratio || !poster) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) setRatio((r) => r || img.naturalWidth / img.naturalHeight);
    };
    img.src = poster;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poster]);

  const playFromStart = (withSound) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.muted = !withSound;
    setMuted(!withSound);
    setEnded(false);
    setStarted(true);
    sessionStorage.setItem(SESSION_KEY, "1");
    el.play().catch(() => {});
  };

  return (
    <div className="mt-10 grid md:grid-cols-[1.15fr_1fr] gap-7 md:gap-10 items-center" data-testid="cassie-intro">
      <div ref={wrapRef} className={`relative transition-all duration-500 ${ended ? "md:max-w-xl" : ""}`}>
        {/* Gradient edge + soft shadow so the clip reads as a brand element, not an embed. */}
        <div className="relative rounded-3xl p-[1.5px] bg-gradient-to-br from-orange/70 via-white/20 to-teal/50 shadow-2xl shadow-navy-deep/50">
          <div
            className="relative rounded-[22px] overflow-hidden bg-navy-deep mx-auto"
            style={
              ratio
                ? ratio >= 1
                  ? { aspectRatio: ratio, width: "100%" }
                  : { aspectRatio: ratio, height: "min(32rem, 70vh)", width: "auto", maxWidth: "100%" }
                : undefined
            }
          >
            <video
              ref={videoRef}
              src={src}
              poster={poster || undefined}
              muted={muted}
              playsInline
              preload="metadata"
              controls={ended}
              aria-label={`${intro?.heading || "Meet Cassie"} — video introduction`}
              onLoadedMetadata={(e) => {
                const { videoWidth: w, videoHeight: h } = e.target;
                if (w && h) setRatio(w / h);
              }}
              onTimeUpdate={(e) => setTime(e.target.currentTime)}
              onEnded={() => setEnded(true)}
              onPlay={() => { setStarted(true); sessionStorage.setItem(SESSION_KEY, "1"); }}
              onError={() => { setFailed(true); setStarted(false); }}
              data-testid="cassie-intro-video"
              className={`block mx-auto bg-navy-deep ${ratio ? "w-full h-full object-contain" : "w-full max-h-[52vh] md:max-h-[26rem] object-contain"}`}
            />

            {/* A clip that won't play leaves the poster and the transcript doing the work. */}
            {failed && poster && (
              <img src={poster} alt={intro?.heading || "Meet Cassie"} className="absolute inset-0 w-full h-full object-contain" />
            )}

            {!started && (
              <button
                type="button"
                onClick={() => playFromStart(!autoplayAllowed)}
                data-testid="cassie-intro-play"
                className="absolute inset-0 flex items-center justify-center bg-navy-deep/40 hover:bg-navy-deep/25 transition-colors"
                aria-label="Play Cassie's introduction"
              >
                <span className="w-16 h-16 rounded-full bg-orange text-white flex items-center justify-center shadow-xl shadow-navy-deep/40 transition-transform hover:scale-105">
                  <Play size={26} className="ml-0.5" />
                </span>
              </button>
            )}

            {caption && !ended && (
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 pointer-events-none">
                <p
                  data-testid="cassie-intro-caption"
                  className="mx-auto max-w-[36rem] text-center text-sm md:text-base font-semibold text-white bg-navy-deep/75 backdrop-blur-sm rounded-xl px-4 py-2 leading-snug"
                >
                  {caption}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {started && muted && !ended && (
            <button
              type="button"
              onClick={() => playFromStart(true)}
              data-testid="cassie-intro-sound"
              className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white text-xs font-bold px-3.5 py-2 rounded-full animate-pulse hover:animate-none transition-colors"
            >
              <Volume2 size={14} /> Tap for sound
            </button>
          )}
          {started && !muted && (
            <button
              type="button"
              onClick={() => { const el = videoRef.current; el.muted = true; setMuted(true); }}
              data-testid="cassie-intro-mute"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3.5 py-2 rounded-full transition-colors"
            >
              <VolumeX size={14} /> Mute
            </button>
          )}
          {(ended || started) && (
            <button
              type="button"
              onClick={() => playFromStart(!muted)}
              data-testid="cassie-intro-replay"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3.5 py-2 rounded-full transition-colors"
            >
              <RotateCcw size={14} /> Replay
            </button>
          )}
          {cues.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCaptions((c) => !c)}
              aria-pressed={showCaptions}
              data-testid="cassie-intro-captions-toggle"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition-colors ${
                showCaptions ? "bg-white text-navy" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <Captions size={14} /> Captions {showCaptions ? "on" : "off"}
            </button>
          )}
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold text-xl md:text-2xl text-white">{intro?.heading || "Meet Cassie"}</h2>
        {intro?.subheading && <p className="text-slate-300 text-sm md:text-base mt-2 leading-relaxed">{intro.subheading}</p>}

        {transcript && (
          <div className="mt-5">
            <button
              type="button"
              onClick={() => setTranscriptOpen((o) => !o)}
              aria-expanded={transcriptOpen}
              data-testid="cassie-intro-transcript-toggle"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-orange hover:text-white transition-colors"
            >
              Read the transcript <ChevronDown size={14} className={`transition-transform ${transcriptOpen ? "rotate-180" : ""}`} />
            </button>
            {/* Always in the HTML so it is readable without JavaScript and indexable. */}
            <p
              data-testid="cassie-intro-transcript"
              className={`text-sm text-slate-300 leading-relaxed mt-3 ${transcriptOpen ? "" : "sr-only"}`}
            >
              {transcript}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
